import {execBI, getBIForEditor} from './bi-events-manager';
import * as appTypeDetector from './app-type-detector';
import * as AppMarketSdk from './wrapped-app-market-sdk';
import * as urls from './url-generator';
import {postProvisionAppToMetaSite} from './market-api';
import {removePendingInMarketAfterInstall} from '../actions/apps-by-type';
import {getClientState, isExperiment} from '../config';
import {popupShow} from '../actions/popup';
import _ from 'lodash';


/**
 * Main add app
 */
export function addApp(app, biPosition = 0, dispatch, config) {
  if (config.market === 'editor') {
    return addAppEditor(app, biPosition, dispatch, config);

  } else if (config.market === 'standalone') {
    return addAppStandalone(app, biPosition, dispatch, config);

  } else if (config.market === 'dashboard') {
    return addAppDashboard(app, biPosition, dispatch, config);
  }
}


/**
 * Actually add app, called after permissions or directly for Wix apps
 */
export function runAddApp(app, dispatch, config) {
  if (config.market === 'editor') {

    if (appTypeDetector.isBusinessApplication(app)) {
      doAddBusinessApp(app, dispatch, config);
    } else {
      doAddApplication(app);
    }

  } else if (config.market === 'standalone') {
    return runAddAppStandalone(app, config);

  } else if (config.market === 'dashboard') {
    return addAppDashboard(app, 0, dispatch);
  }
}


function addAppStandalone(app, biPosition, dispatch, config) {
  if (appTypeDetector.isBusinessApplication(app) && !appTypeDetector.madeByWix(app)) {
    dispatch(popupShow('permissions', app));

  } else {
    runAddAppStandalone(app, config);
  }

  if (appTypeDetector.isBusinessApplication(app)) {
    execBI('ADD_BUSINESS_APP_FLOW', {app, biPosition});
  } else {
    execBI('ADD_APP_CLICK', {app, biPosition, hasPermissions: Number(!appTypeDetector.madeByWix(app))});
  }
}

function runAddAppStandalone(app, config) {
  const button = config.formatMessage({id: 'siteSelector.openSite'});
  const title = config.formatMessage({id: 'siteSelector.title'});
  const isBusiness = appTypeDetector.isBusinessApplication(app);

  const url = isBusiness ? appTypeDetector.getSiteSelectorBusinessAppUrl(app.appDefinitionId, title, button, config) :
    appTypeDetector.getSiteSelectorEditorAppUrl(app.appDefinitionId, title, button);

  if (window.W.Site.Common.UserDetailsServer.isSessionValid()) { // logged in
    window.open(url, isBusiness ? '_self' : '_blank');

  } else {
    location.href = appTypeDetector.getSignInPageUrl(url, config.fullUrl);
  }
}


function addAppDashboard(app) {
  console.log('add business app' + app.slug);
}


/**
 * Add app function called on button click for Editor market
 */
function addAppEditor(app, biPosition = 0, dispatch, config) {

  if (appTypeDetector.alreadyPendingInMarket(app) && appTypeDetector.isBusinessApplication(app)) {
    execBI('OPEN_APP_CLICK', {app, biPosition});
    window.open(urls.getDashboardPendingAppsUrl());
    return null;
  }

  if (appTypeDetector.isBusinessApplication(app)) {
    return addOrOpenBusinessApp(app, biPosition, dispatch, config);
  }

  if (appTypeDetector.shouldOpenEditorApp(app)) {
    return openEditorApp(app, biPosition);
  }

  if (appTypeDetector.madeByWix(app)) {
    return execBI('ADD_APP_CLICK', {app, biPosition, hasPermissions: 0})
      .then(() => doAddApplication(app));
  }

  AppMarketSdk.getIfAppIsInstalled({appDefinitionId: app.appDefinitionId, includingDemoMode: false}, (installed) => {
    if (installed) {
      execBI('ADD_APP_CLICK', {app, biPosition, hasPermissions: 0})
        .then(() => doAddApplication(app));

    } else {
      execBI('ADD_APP_CLICK', {app, biPosition, hasPermissions: 1})
        .then(() => {
          if (isExperiment('specs.market.AppPageInsideMarket', config.experiments)) {
            dispatch(popupShow('permissions', app));
          } else {
            openAppPermissionsModal(app, config);
          }
        });
    }
  });

  return null;
}

/**
 * Provision (post to metaSite that app is been used) Editor market
 */
export function provisionBusinessApp(app, metaSiteId) {
  return postProvisionAppToMetaSite(metaSiteId, app.appDefinitionId, 'MARKET_EDITOR')
    .catch(() => {});
}

export function openEditorApp(app, biPosition = 0) {
  execBI('OPEN_APP_CLICK', {app, biPosition});

  return AppMarketSdk.openApp(app.appDefinitionId);
}

export function openBusinessApp(app, biPosition) {
  const url = urls.getBusinessAppPageUrl(app);
  execBI('OPEN_APP_CLICK', {app, biPosition});
  window.open(url);
}


export function openAppInModal(appSlug, width = 906, height = 576) {
  const url = urls.getInnerOpenAppUrl(appSlug);
  return AppMarketSdk.openAppInfoModal(url, width, height, () => {});
}


export function openAppPermissionsModal(app, config, width = 534, height = 216) {
  const showSecurityNotice = isExperiment('specs.market.SecurePermissions', config.experiments) && !app.listedInMarket;
  height = showSecurityNotice ? 278 : height;

  const url = urls.getInnerAppPermissionsUrl(app);
  return AppMarketSdk.openPermissionsModal(url, width, height, app.appDefinitionId);
}

export function openAppProvisionModal(app, width = 534, height = 469) {
  const url = urls.getInnerProvisionSuccessUrl(app);

  const options = {
    title: '',
    closeModalBiEvent: {evid: 92},
    closeModalBiParams: {'app_id': app.appDefinitionId, 'closing_method': 'x'}
  };

  return AppMarketSdk.openAppMarketModal(url, width, height, 'provisionSuccessMessage', options);
}


function addOrOpenBusinessApp(app, biPosition, dispatch, config) {
  if (app.isInstalled) {
    if (!appTypeDetector.alwaysOnDashboard(app)) {
      return openBusinessApp(app, biPosition);
    }
  }

  execBI('ADD_BUSINESS_APP_FLOW', {app, biPosition});

  return AppMarketSdk.save(() => {

    if (appTypeDetector.madeByWix(app)) {
      return doAddBusinessApp(app, dispatch, config);
    }

    if (isExperiment('specs.market.AppPageInsideMarket', config.experiments)) {
      dispatch(popupShow('permissions', app));
    } else {
      openAppPermissionsModal(app, config);
    }

    return null;
  });
}


/**
 * Adds Business App after permissions
 * Posts to API and opens modal
 * @param app
 * @param dispatch
 */
export function doAddBusinessApp(app, dispatch, config) {
  return provisionBusinessApp(app, config.query.metaSiteId).then(() => {
    if (isExperiment('specs.market.AppPageInsideMarket', config.experiments)) {
      dispatch(popupShow('provision', app));
    } else {
      openAppProvisionModal(app);
    }
  });
}


/**
 * Adds App after permissions
 * Send add app command to editor, after permissions, not for business app
 * @param app
 */
export function doAddApplication(app) {
  if (appTypeDetector.alreadyPendingInMarket(app)) {
    return removePendingInMarketAfterInstall(app)
      .then(() => _doAddApplication(app))
      .catch((err) => console.log(err));

  } else {
    return _doAddApplication(app);
  }
}


function _doAddApplication(app) {

  const biData = getBIForEditor();
  const query = getClientState().mode.query;
  const params = {x: query.x, y: query.y};

  let paramsForApi = {
    'is_permissions_app': !appTypeDetector.madeByWix(app),
    'provision_demo_app': appTypeDetector.isDemoApp()
  };

  if (app.isWixComponent) {
    AppMarketSdk.addComponentToSite(app.wixComponentId, Object.assign(_.omit(biData, 'section'), {'app_id': app.appDefinitionId}));

  } else if (app.appType === 'list') {
    paramsForApi = Object.assign({}, paramsForApi, {
      appDefinitionDataObj: getAppDefinitionObject(app),
      type: 'wixappsPart',
      widgetId: app.listWidgetId,
      x: params.x,
      y: params.y,
      biData
    });
    AppMarketSdk.addAppToSite(paramsForApi);

  } else if (app.hasSection || ((app.section || {}).sectionUrl && appTypeDetector.isPublished(app, app.section))) {
    paramsForApi = Object.assign({}, paramsForApi, {
      appDefinitionDataObj: getAppDefinitionObject(app, 'tpa'),
      type: 'tpaSection',
      widgetId: undefined,
      x: params.x,
      y: params.y,
      biData
    });
    AppMarketSdk.addAppToSite(paramsForApi);

  } else {
    const appDefinition = getAppDefinitionObject(app, 'tpa'), widget = appTypeDetector.getDefaultWidget(app);

    appDefinition.widgets = {};
    appDefinition.widgets[widget.widgetId] = widget;
    paramsForApi = Object.assign({}, paramsForApi, {
      appDefinitionDataObj: appDefinition,
      type: 'tpaWidget',
      widgetId: widget.widgetId,
      x: params.x,
      y: params.y,
      biData
    });
    AppMarketSdk.addAppToSite(paramsForApi);
  }

  // TODO remove this after AppPageInsideMarket experiment
  AppMarketSdk.closeAppPopup(); // closes App Modal (it's open or not)
  return null;
}


function getAppDefinitionObject(app, type) {
  return {
    appDefinitionId: app.appDefinitionId,
    appName: app.name,
    price: app.price,
    upgradeBenefits: app.upgradeBenefits,
    supportInfo: app.supportInfo,
    type: app.type || type,
    doNotShowPageAddedPanel: app.hideAppFirstTimeMsg,
    panelInfo: {
      title: 'application.panelInfo.title',
      features: app.features
    }
  };
}

export function addAppClientData(app, isInstalledSdk) {
  const metaSiteApps = getClientState().mode.metaSiteApps.items;
  const myAppData = _.find(metaSiteApps, (i => i.clientSpec && i.clientSpec.appDefinitionId === app.appDefinitionId));

  const pendingInMarketApps = getClientState().appsByType.pendingInMarket.items;
  const pendingInMarket = !!_.find(pendingInMarketApps, i => i.guid === app.appDefinitionId);

  const isInstalled = isInstalledSdk || (!!appTypeDetector.isBusinessApplication(app) && !!myAppData);
  return Object.assign({}, app, {isInstalled, myAppData, pendingInMarket});
}



// WEBPACK FOOTER //
// ./services/app-flow.js